import { useDesignEditorStateContext } from "../contexts/design-editor-state-provider.context";
import { DesignEditorStates } from "../models/enums/design-editor-states.enum";
import { StateBasedPanel } from "./panel/state-based-panel.component";
import { StateBasedView } from "./state-based-view.component";
import { JetSportBox } from "../../../components/BoxView/JetSportBox";
import LoadingElement from "../../../components/loading-element/loading-element";
import { StrucRow } from "../../../components/StructureElements/StrucRow";
import { StrucCol } from "../../../components/StructureElements/StrucCol";
import { BoxHeader2 } from "../../../components/BoxView/BoxHeader2";
import { BoxHeaderTypography } from "../../../components/Typographies/BoxHeaderTypography";
import { WhiteLabelIcon } from "../../logo-editor/componenets/WhiteLabelIcon";
import {
  fa3,
  faCheck,
  faFolder,
  faFont,
  faPalette,
  faPen,
  faSave,
  faUser,
} from "@fortawesome/free-solid-svg-icons";
import { BoxContent } from "../../../components/BoxView/BoxContent";
import { MenuButton } from "./menu-button.component";
import { PrimaryButton } from "../../../components/Buttons/PrimaryButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState } from "react";
import { Design } from "../../../../core/models/entities/Design";
import { LogoOptions } from "../../../../core/models/entities/LogoOptions";
import { useDesignContext } from "../contexts/design.context";
import { CheckboxOptions } from "../../../components/Input/CheckboxOptions";
import { MainViewStates } from "../models/enums/main-view-states.enum";
import { SecondaryButton } from "../../../components/Buttons/SecondaryButton";
import { useProfileContext } from "../../../../contexts/ProfileContext";
import { Role } from "../../../../core/models/entities/Role";

export interface DesignEditorToolProps {
  design: Design;
  options: LogoOptions;
}

export function DesignEditorTool({ design }: DesignEditorToolProps) {
  const { sidePanelState, goToState, mainViewState, overall, isLoading } =
    useDesignEditorStateContext();
  const {
    updateDesign,
    approveDesign,
    approvalDialog,
    readonly,
    handleGoToChangeStyle,
  } = useDesignContext();
  const { profile } = useProfileContext();
  const [confirmApproval, setConfirmApproval] = useState<boolean>(false);

  const role = profile?.userOrganizationInformations.role;
  const isAdmin = role === Role.Admin;

  const neutral = overall === DesignEditorStates.Normal;
  const showSaveConfirmDesign = mainViewState === MainViewStates.LogoPlacer;

  const actions = (
    <>
      <StrucRow>
        <div className={"w-full flex justify-between px-8 pb-5 pt-7"}>
          <div className="flex gap-x-6">
            <PrimaryButton
              className={"px-4"}
              onClick={updateDesign}
              disabled={readonly}
            >
              <FontAwesomeIcon icon={faSave} />
              <span className={"flex-1"}>Save Draft</span>
            </PrimaryButton>
            <SecondaryButton
              className={"px-4"}
              onClick={() => handleGoToChangeStyle(design)}
              disabled={readonly}
            >
              <FontAwesomeIcon icon={faPen} />
              <span className={"flex-1"}>Change Style</span>
            </SecondaryButton>
          </div>
          {showSaveConfirmDesign && (
            <div className={"space-x-4 flex"}>
              {!isAdmin && (
                <CheckboxOptions
                  header={"YES, I AM SURE I AM DONE"}
                  name={"confirmApproval"}
                  checked={confirmApproval}
                  onChange={(x) => setConfirmApproval(x.target.checked)}
                  explain={""}
                  disabled={false || readonly}
                  checkboxPosition="left"
                ></CheckboxOptions>
              )}
              <PrimaryButton
                width={"md"}
                className={"px-4"}
                onClick={approveDesign}
                disabled={(!confirmApproval && !isAdmin) || readonly}
              >
                <FontAwesomeIcon icon={faCheck} />
                <span className={"flex-1"}>Save Changes & Approve</span>
              </PrimaryButton>
            </div>
          )}
        </div>
      </StrucRow>
    </>
  );

  return (
    <>
      {approvalDialog}
      <JetSportBox>
        {isLoading && <LoadingElement transparent />}
        <StrucRow className={"border-b"}>
          <StrucCol className={"border-r"} size={4}>
            <BoxHeader2 className={"justify-between items-center"}>
              <BoxHeaderTypography>Editing Design</BoxHeaderTypography>
              <WhiteLabelIcon icon={faPalette} />
            </BoxHeader2>
            <BoxContent>
              <div className={"space-y-2"}>
                <StrucRow className={"space-x-2"}>
                  <StrucCol>
                    <MenuButton
                      icon={faUser}
                      text={"CREATE NAME"}
                      className={
                        overall !== DesignEditorStates.AddName && !neutral
                          ? "opacity-70"
                          : undefined
                      }
                      onClick={() =>
                        goToState(
                          overall === DesignEditorStates.AddName
                            ? DesignEditorStates.Normal
                            : DesignEditorStates.AddName
                        )
                      }
                    />
                  </StrucCol>
                  <StrucCol>
                    <MenuButton
                      icon={faFont}
                      text={"CREATE TEXT"}
                      className={
                        overall !== DesignEditorStates.AddText && !neutral
                          ? "opacity-70"
                          : undefined
                      }
                      onClick={() =>
                        goToState(
                          overall === DesignEditorStates.AddText
                            ? DesignEditorStates.Normal
                            : DesignEditorStates.AddText
                        )
                      }
                    />
                  </StrucCol>
                </StrucRow>
                <StrucRow className={"space-x-2"}>
                  <StrucCol>
                    <MenuButton
                      icon={fa3}
                      text={"CREATE NUMBERSET"}
                      className={
                        overall !== DesignEditorStates.AddNumber && !neutral
                          ? "opacity-70"
                          : undefined
                      }
                      onClick={() =>
                        goToState(
                          overall === DesignEditorStates.AddNumber
                            ? DesignEditorStates.Normal
                            : DesignEditorStates.AddNumber
                        )
                      }
                    />
                  </StrucCol>
                  <StrucCol>
                    <MenuButton
                      icon={faFolder}
                      text={"VIEW LIBRARY"}
                      className={
                        overall !== DesignEditorStates.ViewLibrary && !neutral
                          ? "opacity-70"
                          : undefined
                      }
                      onClick={() =>
                        goToState(
                          overall === DesignEditorStates.ViewLibrary
                            ? DesignEditorStates.Normal
                            : DesignEditorStates.ViewLibrary
                        )
                      }
                    />
                  </StrucCol>
                </StrucRow>
              </div>
              <StateBasedPanel state={sidePanelState} />
            </BoxContent>
          </StrucCol>
          <StrucCol size={8} className="overflow-hidden">
            {mainViewState === MainViewStates.LogoPlacer && actions}
            <StateBasedView state={mainViewState} />
          </StrucCol>
        </StrucRow>
      </JetSportBox>
    </>
  );
}
